import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import s from "./ApplicationsList.module.scss";
import { Input, Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CreditCardOutlined,
  EditOutlined,
  MailOutlined,
} from "@ant-design/icons";
import type { TableProps } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getApplicationDetails,
  getApplicationsList,
  getCountriesWithPosts,
  getManagerServicesByCountry,
  getMessagesList,
  searchApplications,
  setCurrentPageNumber,
  setLastSearchValue,
} from "../../redux/ApplicationSlice";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import Footer from "../../components/Footer/Footer";
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import useWindowDimensions from "../../components/WindowDimensions/WindowDimensions";
import { getStatusTag } from "../../utils/functions";
import MobileApplicationsList from "../MobileApplicationsList/MobileApplicationsList";
import { ICreateApplicationResponse } from "../../types";
import Loading from "../../components/Loading/Loading";
import { getExchangeRate } from "../../redux/BillingSlice";
import { useTranslation } from "react-i18next";

import { getStatus, TDropdown } from "../../data";
import { Root } from "react-dom/client";
import TabPane from "antd/es/tabs/TabPane";
import type { GetProps } from "antd";
interface TableType {
  number: number;
  status: string;
  application_number: string;
  registration_date: string;
  user?: string;
}
type Props = {};
const { Search } = Input;
type SearchProps = GetProps<typeof Input.Search>;

const ApplicationsList = (props: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getManagerServicesByCountry());
    dispatch(getCountriesWithPosts());
  }, [dispatch]);

  const { width } = useWindowDimensions();
  const activeTab = useSelector(
    (state: RootState) => state.application.activeTab,
  );
  const applicationsList = useSelector(
    (state: RootState) => state.application.applicationsList,
  );

  const user = useSelector((state: RootState) => state.user.user);

  const [data, setData] = useState<ICreateApplicationResponse[] | undefined>(
    applicationsList?.results,
  );

  const currentStatePage = useSelector(
    (state: RootState) => state.application.currentPage,
  );

  const [currentPage, setCurrentPage] = useState<number>(currentStatePage || 1);

  const lang = useSelector((state: RootState) => state.application.lang);
  const { t } = useTranslation();
  const statusOptions = getStatus(t);

  const [pagination, setPagination] = useState({
    current: applicationsList?.pagination?.current_page || 1,
    pageSize: applicationsList?.pagination?.page_size || 30,
    total: applicationsList?.pagination?.total_items || 100,
  });

  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const fetchUserData = async ({ page }: { page: number }) => {
      const res = await dispatch(getApplicationsList(page));

      if (
        res.payload &&
        res.type === "applications/getApplicationsList/fulfilled"
      ) {
        if ("results" in res.payload) {
          setData(res.payload.results);
          setPagination({
            current: res.payload.pagination?.current_page,
            pageSize: res.payload.pagination?.page_size,
            total: res.payload.pagination?.total_items,
          });
        } else {
          console.error("Unexpected API response:", res.payload);
        }
      }
    };

    const fetchData = async ({ page }: { page: number }) => {
      const res = await dispatch(getApplicationsList(page));

      if (
        res.payload &&
        res.type === "applications/getApplicationsList/fulfilled"
      ) {
        if ("results" in res.payload) {
          setData(res.payload.results);
          setPagination({
            current: res.payload.pagination?.current_page,
            pageSize: res.payload.pagination?.page_size,
            total: res.payload.pagination?.total_items,
          });
        } else {
          console.error("Unexpected API response:", res.payload);
        }
      }
    };

    fetchUserData({
      page: currentPageRef.current,
    });

    const interval = setInterval(() => {
      fetchUserData({
        page: currentPageRef.current,
      });
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch]);

  const getNextPage = async (page: number) => {
    setCurrentPage(page);
    dispatch(setCurrentPageNumber(page));
    currentPageRef.current = page;

    if (page === undefined || page === null) return;

    try {
      const res = await dispatch(getApplicationsList(page));
      if (!res.payload || !("results" in res.payload)) {
        console.error("Unexpected API response:", res);
        return;
      }

      if (res.type === "applications/getApplicationsList/fulfilled") {
        if ("results" in res.payload) {
          setData(res.payload.results);
          setPagination({
            current: res?.payload?.pagination?.current_page,
            pageSize: res?.payload?.pagination.page_size,
            total: res.payload.pagination.total_items,
          });
        } else {
          console.error("Unexpected API response:", res.payload);
        }
      }
    } catch (error) {
      console.error("Error in getNextPage:", error);
    }
  };

  const columns: ColumnsType<TableType> = [
    {
      title: "№",
      dataIndex: "number",
      width: 20,
      align: "center",
    },
    {
      title: t("Статус"),
      dataIndex: `status`,
      width: 400,
      render: (value: string) => getStatusTag(value, "lg", t),
      filters: statusOptions?.map(({ value, label }) => ({
        text: label,
        value: value,
      })),
      onFilter: (value, record) =>
        record?.status?.indexOf(value as string) === 0,
    },
    {
      title: t("Номер заявки"),
      dataIndex: `application_number`,
      width: 600,
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Номер транспортного средства"),
      dataIndex: `vehicle_number`,
      width: 600,
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Дата регистрации"),
      dataIndex: `registration_date`,
      width: 600,
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Заявление"),
      dataIndex: `application`,
      width: 50,
    },
    {
      title: t("Оплата"),
      dataIndex: `payment`,
      width: 50,
      align: "center",
    },
    {
      title: t("История"),
      dataIndex: `history`,
      width: 0,
    },
    {
      title: t("Декларация"),
      dataIndex: `declaration`,
      width: 0,
    },
  ];

  const columns2: ColumnsType<TableType> = [
    {
      title: "№",
      dataIndex: "number",
      width: 20,
      align: "center",
    },
    {
      title: t("Статус"),
      dataIndex: `status`,
      width: 400,
      render: (value: string) => getStatusTag(value, "lg", t),

      filters: statusOptions.map(({ label }) => ({
        text: label,
        value: label,
      })),

      onFilter: (value, record) =>
        record?.status?.indexOf(value as string) === 0,
    },
    {
      title: user?.role === "regional_manager" ? t("Сотрудник") : t("ФИО"),
      dataIndex: `name`,
      width: 600,
    },
    {
      title: t("Номер заявки"),
      dataIndex: `application_number`,
      width: 600,
      render: (text) => <div className={s.font}>{text}</div>,
    },

    {
      title: t("Дата регистрации"),
      dataIndex: `registration_date`,
      width: 600,
      render: (text) => <div className={s.font}>{text}</div>,
    },

    {
      title: t("Заявление"),
      dataIndex: `application`,
      width: 50,
    },

    {
      title: t("История"),
      dataIndex: `history`,
      width: 50,
    },
    {
      title: t("Декларация"),
      dataIndex: `declaration`,
      width: 0,
    },
  ];

  const columns3: ColumnsType<TableType> = [
    {
      title: "№",
      dataIndex: "number",
      width: 20,
      align: "center",
    },
    {
      title: t("Статус"),
      dataIndex: `status`,
      width: 200,
      render: (value: string) => getStatusTag(value, "lg", t),

      filters: statusOptions?.map(({ value, label }) => ({
        text: label,
        value: value,
      })),
      onFilter: (value, record) =>
        record?.status?.indexOf(value as string) === 0,
    },
    {
      title: t("ФИО"),
      dataIndex: `name`,
      width: 300,
    },
    {
      title: t("Номер заявки"),
      dataIndex: `application_number`,
      width: 150,
      render: (text) => <div className={s.font}>{text}</div>,
    },

    {
      title: t("Дата регистрации"),
      dataIndex: `registration_date`,
      width: 180,
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Декларант"),
      dataIndex: `declarant`,
      width: 200,
    },

    {
      title: t("Заявление"),
      dataIndex: `application`,
      width: 50,
    },
    {
      title: t("Оплата"),
      dataIndex: `payment`,
      width: 50,
      align: "center",
    },

    {
      title: t("История"),
      dataIndex: `history`,
      width: 0,
    },
    {
      title: t("Декларация"),
      dataIndex: `declaration`,
      width: 0,
    },
  ];

  const getPaymentAmount = async (id: number) => {
    const res = await dispatch(getApplicationDetails(id));
    if (res.type === "applications/getApplicationDetails/fulfilled") {
      navigate(`/payment/${id}`);
    }
  };

  const getMessages = async (id: number) => {
    const res = await dispatch(getMessagesList(Number(id)));

    if (res.type === "messages/getMessagesList/fulfilled") {
      navigate(`/messages/${id}`);
    }
  };
  const onSearch: SearchProps["onSearch"] = async (value, _e, info) => {
    const res = await dispatch(
      searchApplications({ search_term: value, page: 1 }),
    );

    dispatch(setLastSearchValue(value));
    console.log("res", res);
    if (res.type === "applications/searchApplications/fulfilled") {
      navigate("/search-results");
    }
  };

  return (
    <div>
      {width > 985 ? <Header /> : <MobileHeader />}
      <div className={s.header}>
        <div className={s.top_header}>
          <div className={s.h2}>{t("Все заявления")}</div>
          <div className={s.search_block}>
            {(user?.role === "broker" || user?.role === "admin") && (
              <Search
                placeholder={t("Поиск")}
                allowClear
                enterButton
                size="small"
                onSearch={onSearch}
                className={s.search}
              />
            )}
          </div>
        </div>

        {user?.role === "driver" ? (
          <div className={s.user_btn}>
            <Link
              to="/createApplication"
              className={s.btn}
              onClick={() => {
                dispatch(getCountriesWithPosts());
                dispatch(getManagerServicesByCountry());
              }}
            >
              {t("Зарегистрировать новую заявку")}
            </Link>
          </div>
        ) : null}
      </div>
      <div className={s.content}>
        <div className={s.form}>
          {width > 700 ? (
            <Table<TableType>
              bordered
              pagination={pagination}
              columns={columns}
              className="table"
              onChange={(pagination) => {
                if (pagination?.current) {
                  getNextPage(pagination.current);
                }
              }}
              dataSource={
                data &&
                data.map((it, index) => {
                  const routeServices =
                    it.routes?.flatMap((route) =>
                      route.services.map((service) => ({
                        routeCountry: route.transit_country_name,
                        serviceName: service.service_name,
                        status: service.status,
                      })),
                    ) || [];

                  return {
                    key: it.id,
                    number: index + 1,
                    status: it.status,
                    application_number: it.application_number,
                    vehicle_number: it.vehicle_number,
                    registration_date: moment(it.created_at).format(
                      "DD.MM.YYYY - HH:mm",
                    ),

                    services: (
                      <div>
                        {routeServices.map((srv, idx) => (
                          <div key={idx}>
                            {srv.routeCountry} — {srv.serviceName} —{" "}
                            {srv.status}
                          </div>
                        ))}
                      </div>
                    ),

                    // остальные поля ↓
                    payment:
                      it.status === "new" ||
                      it.status === "incomplete" ||
                      it.status === "corrected" ? (
                        <div>-</div>
                      ) : (
                        <div
                          onClick={() => {
                            getPaymentAmount(it.id);
                            dispatch(getExchangeRate());
                          }}
                        >
                          <Tooltip title={t("Просмотреть оплату")} key={2}>
                            <CreditCardOutlined style={{ color: "gray" }} />
                          </Tooltip>
                        </div>
                      ),

                    application: (
                      <Link
                        to={
                          user?.role === "driver"
                            ? `/application/${it.id}`
                            : `/broker/application/${it.id}`
                        }
                        className={s.edit}
                        onClick={() => dispatch(getApplicationDetails(it.id))}
                      >
                        <Tooltip title={t("Просмотр/Редактирование")} key={1}>
                          <EditOutlined />
                        </Tooltip>
                      </Link>
                    ),

                    history: (
                      <div
                        onClick={() => getMessages(it.id)}
                        className={s.edit}
                      >
                        <Tooltip title={t("Просмотреть историю")} key={2}>
                          <MailOutlined />
                        </Tooltip>
                      </div>
                    ),

                    declaration: <div className={s.edit}></div>,
                  };
                })
              }

              // dataSource={

              //   data &&
              //   data.map((it, index) => ({
              //     key: it.id,
              //     number: index + 1,
              //     status: it.status,
              //     application_number: it?.application_number,
              //     vehicle_number: it?.vehicle_number,
              //     registration_date: moment(it?.created_at).format(
              //       "DD.MM.YYYY - HH:mm"
              //     ),

              //     payment:
              //       it?.status === "new" ||
              //       it?.status === "incomplete" ||
              //       it?.status === "corrected" ? (
              //         <div> {"-"} </div>
              //       ) : (
              //         <div
              //           onClick={() => {
              //             getPaymentAmount(it.id);
              //             dispatch(getExchangeRate());
              //           }}
              //         >
              //           <Tooltip title={t("Просмотреть оплату")} key={2}>
              //             <CreditCardOutlined style={{ color: "gray" }} />
              //           </Tooltip>
              //         </div>
              //       ),
              //     application: (
              //       <Link
              //         to={
              //           user?.role === "driver"
              //             ? `/application/${it?.id}`
              //             : `/broker/application/${it?.id}`
              //         }
              //         className={s.edit}
              //         onClick={() => {
              //           dispatch(getApplicationDetails(it.id));
              //         }}
              //       >
              //         <Tooltip title={t("Просмотр/Редактирование")} key={1}>
              //           <EditOutlined />
              //         </Tooltip>
              //       </Link>
              //     ),
              //     history: (
              //       <div onClick={() => getMessages(it.id)} className={s.edit}>
              //         {/* <Link to={`/messages/${it.id}`} className={s.edit}> */}
              //         <Tooltip title={t("Просмотреть историю")} key={2}>
              //           <MailOutlined />
              //           {/* {user?.role === "driver" &&
              //               it?.unread_message_count_owner > 0 && (
              //                 <div className={s.messages_count}>
              //                   {it.unread_message_count_owner}
              //                 </div>
              //               )} */}
              //           {/* {user?.role === "broker" &&
              //               it?.unread_message_count_declarant > 0 && (
              //                 <div className={s.messages_count}>
              //                   {it.unread_message_count_declarant}
              //                 </div>
              //               )} */}
              //         </Tooltip>

              //         {/* </Link> */}
              //       </div>
              //     ),
              //     declaration: (
              //       <div className={s.edit}>
              //         {/* {it.declaration ? (
              //             <Link to={it.declaration} className={s.edit}>
              //               <Tooltip title={t("Декларация")} key={4}>
              //                 <FileDoneOutlined />
              //               </Tooltip>
              //             </Link>
              //           ) : (
              //             "-"
              //           )} */}
              //       </div>
              //     ),
              //   }))
              // }
            />
          ) : (
            <MobileApplicationsList />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ApplicationsList;
